// A matrix (P3): questions down the side, answers across the top, a radio (or
// a checkbox when `multi`) in every cell. The answer is one object keyed by
// row value; a row with nothing picked is left out of it, so an untouched
// grid stays blank. A table on a desktop; at 768px and under each row stacks
// and the cells name their column in a label that is also the tap target
// (M2/C3). The group wrapper is FieldInput's.
import type { FieldDefinition } from '../shared/types';
import { matrixAnswer, matrixMeta } from './questionTypes';

export default function MatrixInput({ def, domId, value, onChange, disabled }: {
  def: FieldDefinition;
  domId: string;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
}) {
  const meta = matrixMeta(def.ui_meta);
  const answer = matrixAnswer(value) || {};
  const picked = (row: string, col: string) => {
    const cell = answer[row];
    return (Array.isArray(cell) ? cell : [cell]).some((x) => String(x) === col);
  };
  const set = (row: string, col: string) => {
    const next: Record<string, unknown> = { ...answer };
    if (meta.multi) {
      const cur = Array.isArray(answer[row]) ? (answer[row] as string[]) : [];
      const list = cur.includes(col) ? cur.filter((x) => x !== col) : [...cur, col];
      if (list.length) next[row] = list; else delete next[row];
    } else {
      next[row] = col;
    }
    onChange(next);
  };
  if (!meta.rows.length || !meta.columns.length) return null;
  return (
    <table className="of-matrix">
      <thead>
        <tr>
          <th scope="col" className="of-matrix-corner" />
          {meta.columns.map((c) => <th key={c.value} scope="col">{c.text}</th>)}
        </tr>
      </thead>
      <tbody>
        {meta.rows.map((r) => (
          <tr key={r.value}>
            <th scope="row">{r.text}</th>
            {meta.columns.map((c) => (
              <td key={c.value} data-col={c.text}>
                <input
                  type={meta.multi ? 'checkbox' : 'radio'}
                  id={`${domId}-${r.value}-${c.value}`}
                  name={`${domId}-${r.value}`}
                  checked={picked(r.value, c.value)}
                  disabled={disabled}
                  aria-label={`${r.text}: ${c.text}`}
                  onChange={() => set(r.value, c.value)}
                />
                {/* The column's name beside the control in stacked mode, and a
                    44px target with it. It used to be a `td::after`, which
                    reads the same and cannot be tapped. Hidden above the
                    stacked breakpoint, where the <thead> names the column. */}
                <label className="of-matrix-col" htmlFor={`${domId}-${r.value}-${c.value}`}>{c.text}</label>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
